import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [borrowCount, setBorrowCount] = useState({ active_count: 0, max_limit: 5, remaining: 5 });

  const fetchBorrowCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/borrows/count`, {
        withCredentials: true
      });
      setBorrowCount(data);
    } catch (error) {
      console.error('Failed to fetch borrow count:', error);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(data);
      await fetchBorrowCount();
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [fetchBorrowCount]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (identifier, password) => {
    try {
      const isEmail = /@/.test(identifier);
      const payload = isEmail
        ? { email: identifier, password }
        : { membership_id: identifier, password };
      const { data } = await axios.post(
        `${API}/auth/login`,
        payload,
        { withCredentials: true }
      );
      setUser(data);
      await fetchBorrowCount();
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Login failed. Please try again.';

      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
      }

      return { success: false, error: message };
    }
  }, [fetchBorrowCount]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(false);
      setBorrowCount({ active_count: 0, max_limit: 5, remaining: 5 });
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, []);

  const contextValue = useMemo(
    () => ({ user, loading, login, logout, checkAuth, borrowCount, fetchBorrowCount }),
    [user, loading, login, logout, checkAuth, borrowCount, fetchBorrowCount]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
